/**
 * Admin Activity Log - Global activity tracking for owners
 * Provides oversight of all team activities across all cases
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import AppLayout from '../components/AppLayout';
import { formatCurrency } from '../utils/formatCurrency';
import axios from 'axios';
import {
  Activity, Search, Filter, Download, Calendar, User, FileText,
  Eye, Edit3, Share2, Trash2, MessageSquare, Copy, Zap, 
  ChevronDown, ChevronLeft, ChevronRight, RefreshCw, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ActivityItem {
  id: string;
  report_id: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  action_type: string;
  action_details: string;
  section?: string;
  ip_address?: string;
  user_agent?: string;
  metadata?: Record<string, any>;
  timestamp: string;
  // Joined data
  client_name?: string;
  business_name?: string;
}

interface ActivityStats {
  total_activities: number;
  activities_today: number;
  unique_users: number;
  most_active_user: string;
  action_breakdown: Record<string, number>;
}

export default function AdminActivityLog() {
  const { getToken } = useAuth();
  const { role } = usePermissions();
  
  // Data state
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7'); // days
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 50;
  
  // Unique users for filter dropdown
  const [uniqueUsers, setUniqueUsers] = useState<{email: string; name?: string}[]>([]);

  // Action type configurations
  const actionConfig: Record<string, { icon: React.ReactNode; color: string; label: string; bgColor: string }> = {
    view: { icon: <Eye className="w-4 h-4" />, color: 'text-blue-700', bgColor: 'bg-blue-100', label: 'Viewed' },
    edit: { icon: <Edit3 className="w-4 h-4" />, color: 'text-amber-700', bgColor: 'bg-amber-100', label: 'Edited' },
    share: { icon: <Share2 className="w-4 h-4" />, color: 'text-green-700', bgColor: 'bg-green-100', label: 'Shared' },
    download: { icon: <Download className="w-4 h-4" />, color: 'text-purple-700', bgColor: 'bg-purple-100', label: 'Downloaded' },
    generate: { icon: <Zap className="w-4 h-4" />, color: 'text-purple-700', bgColor: 'bg-purple-100', label: 'Generated' },
    delete: { icon: <Trash2 className="w-4 h-4" />, color: 'text-red-700', bgColor: 'bg-red-100', label: 'Deleted' },
    duplicate: { icon: <Copy className="w-4 h-4" />, color: 'text-teal-700', bgColor: 'bg-teal-100', label: 'Duplicated' },
    comment: { icon: <MessageSquare className="w-4 h-4" />, color: 'text-gray-700', bgColor: 'bg-gray-100', label: 'Commented' },
    invite: { icon: <User className="w-4 h-4" />, color: 'text-cyan-700', bgColor: 'bg-cyan-100', label: 'Invited' },
    add_member: { icon: <User className="w-4 h-4" />, color: 'text-emerald-700', bgColor: 'bg-emerald-100', label: 'Added Member' },
    remove_member: { icon: <User className="w-4 h-4" />, color: 'text-rose-700', bgColor: 'bg-rose-100', label: 'Removed Member' },
    update_role: { icon: <User className="w-4 h-4" />, color: 'text-orange-700', bgColor: 'bg-orange-100', label: 'Updated Role' },
    calculate: { icon: <Zap className="w-4 h-4" />, color: 'text-violet-700', bgColor: 'bg-violet-100', label: 'Calculated' },
  };

  // Load activities
  const loadActivities = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());
      params.append('days', dateRange);
      if (userFilter !== 'all') params.append('user_email', userFilter);
      if (actionFilter !== 'all') params.append('action_type', actionFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/activity/admin/all?${params.toString()}`,
        { headers }
      );
      
      setActivities(response.data.activities || []);
      setTotalPages(response.data.total_pages || 1);
      setStats(response.data.stats || null);
      setUniqueUsers(response.data.unique_users || []);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load activities:', err);
      setError(err.response?.data?.detail || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, [page, userFilter, actionFilter, dateRange]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadActivities();
      } else {
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Email', 'Action', 'Details', 'Report/Client', 'IP Address'];
    const rows = activities.map(a => [
      new Date(a.timestamp).toISOString(),
      a.user_name || 'Unknown',
      a.user_email,
      a.action_type,
      a.action_details || '',
      a.client_name || a.report_id,
      a.ip_address || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Check permissions - only owners can access
  if (role !== 'owner' && role !== 'admin') {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">Access Restricted</h2>
            <p className="text-gray-500 mt-2">Only owners can access the activity log.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Activity className="w-7 h-7 text-blue-600" />
              Activity Log
            </h1>
            <p className="text-gray-600 mt-1">Monitor team activity across all cases</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={loadActivities}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Total Activities</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_activities.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Today</p>
              <p className="text-2xl font-bold text-blue-600">{stats.activities_today.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.unique_users}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Most Active</p>
              <p className="text-lg font-semibold text-gray-900 truncate">{stats.most_active_user || 'N/A'}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user, client, report ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            
            {/* User Filter */}
            <select
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Users</option>
              {uniqueUsers.map(u => (
                <option key={u.email} value={u.email}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
            
            {/* Action Type Filter */}
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Actions</option>
              <option value="view">Views</option>
              <option value="edit">Edits</option>
              <option value="download">Downloads</option>
              <option value="share">Shares</option>
              <option value="generate">Generations</option>
              <option value="comment">Comments</option>
              <option value="delete">Deletions</option>
              <option value="invite">Invites</option>
            </select>
            
            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="1">Today</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            
            {/* Clear Filters */}
            {(searchQuery || userFilter !== 'all' || actionFilter !== 'all' || dateRange !== '7') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setUserFilter('all');
                  setActionFilter('all');
                  setDateRange('7');
                  setPage(1);
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Activity Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Activity className="w-12 h-12 text-gray-300 mb-3" />
              <p>No activities found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Details</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Case/Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activities.map((activity) => {
                      const config = actionConfig[activity.action_type] || {
                        icon: <Activity className="w-4 h-4" />,
                        color: 'text-gray-700',
                        bgColor: 'bg-gray-100',
                        label: activity.action_type
                      };
                      
                      return (
                        <tr key={activity.id} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{formatTime(activity.timestamp)}</div>
                            <div className="text-xs text-gray-500">{formatFullDate(activity.timestamp)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{activity.user_name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{activity.user_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
                              {config.icon}
                              {config.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-700 max-w-xs truncate" title={activity.action_details}>
                              {activity.action_details || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {activity.client_name ? (
                              <div>
                                <div className="text-sm font-medium text-gray-900">{activity.client_name}</div>
                                <div className="text-xs text-gray-500 font-mono">{activity.report_id?.slice(0, 8)}...</div>
                              </div>
                            ) : activity.report_id === 'system' ? (
                              <span className="text-xs text-gray-400 italic">System</span>
                            ) : (
                              <span className="text-xs text-gray-500 font-mono">{activity.report_id?.slice(0, 8)}...</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500 font-mono">{activity.ip_address || '-'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
